import { useEffect, useState } from 'react'

import { TSearchRoom, useSearchFilterStore } from '@/entities/map'
import {
  Overview,
  RoomThumbnail,
  SuperHostBadge,
  PriceDetail,
  DiscountBadge,
} from '@/entities/room'
import { LikeButton } from '@/features/toggle-favorite-room'
import { Link } from '@/shared/i18n'
import { cn, useCircleOverlay, getURLWithParams } from '@/shared/lib'

type Props = {
  room: TSearchRoom
}

export const RoomItem = ({ room }: Props) => {
  const [isHovered, setIsHovered] = useState(false)

  const { showCircle, hideCircle } = useCircleOverlay()

  const startDate = useSearchFilterStore(state => state.filters.startDate)
  const endDate = useSearchFilterStore(state => state.filters.endDate)

  const handleMouseEnter = () => {
    setIsHovered(true)
    showCircle({ lat: room.lat, lng: room.lng })
  }

  const handleMouseLeave = () => {
    setIsHovered(false)
    hideCircle()
  }

  useEffect(() => {
    return () => {
      setIsHovered(false)
      hideCircle()
    }
  }, [])

  return (
    <Link
      className="block grow cursor-pointer flex-col space-y-3"
      href={getURLWithParams(`/guest/room/${room.rid}`, {
        startDate,
        endDate,
      })}
      target="_blank"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="relative">
        <div className="absolute top-2 left-2 z-[1] flex gap-1">
          {room.isSuperHost && <SuperHostBadge />}
        </div>
        <LikeButton
          roomId={room.rid}
          like={room.like}
          className="absolute top-0 right-0 z-[1]"
        />
        <RoomThumbnail
          src={room.picMain}
          alt={room.roomName}
          rounded
          size={175}
          ratio="3:2"
          className="!w-full"
          imageClassName={cn(
            isHovered && 'scale-105 transition-all duration-300',
          )}
          unoptimized
        />
      </div>
      <div>
        <Overview
          size="large"
          province={room.province}
          town={room.town}
          propertyType={room.propertyType}
          roomName={room.roomName}
          roomCnt={room.roomCnt}
          bathroomCnt={room.bathroomCnt}
          cookroomCnt={room.cookroomCnt}
          sittingroomCnt={room.sittingroomCnt}
          usingFee={room.usingFee}
          mgmtFee={room.mgmtFee}
        />
        <PriceDetail usingFee={room.usingFee} mgmtFee={room.mgmtFee} />
        <DiscountBadge
          percent={room.longtermDiscountPer}
          amount={room.earlyDiscountAmount}
        />
      </div>
    </Link>
  )
}
